import React, { forwardRef, useCallback, useRef, useState } from "react";
import classNames from "classnames";
import { graphql } from "gatsby";
import slugify from "@sindresorhus/slugify";
import { Swiper, SwiperSlide } from "swiper/react";
import { Scrollbar, Navigation } from "swiper/modules";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { Link } from "./link";
import { eventInternalLinkClick } from "../utility/datalayer";

const SwiperPrevBtn = forwardRef((swiper, ref) => {
  return (
    <button
      ref={ref}
      className={classNames("swiper-button-prev swiper-button-prev--category-module", {
        "d-none": !swiper?.swiper,
        "swiper-button-lock": swiper?.swiper?.isLocked,
        "swiper-button-disabled": swiper?.swiper?.activeIndex === 0,
      })}
      onClick={() => swiper?.swiper?.slidePrev()}
      aria-label="prev"
    ></button>
  );
});

const SwiperNextBtn = forwardRef((swiper, ref) => {
  return (
    <button
      ref={ref}
      className={classNames("swiper-button-next swiper-button-next--category-module", {
        "d-none": !swiper?.swiper,
        "swiper-button-lock": swiper?.swiper?.isLocked,
        "swiper-button-disabled":
          swiper?.swiper?.activeIndex === swiper?.swiper?.slides?.length - 1,
      })}
      onClick={() => swiper?.swiper?.slideNext()}
      aria-label="next"
    ></button>
  );
});

const CategoryModule = ({ data, pagePath }) => {
  const [swiperData, setSwiperData] = useState(null);
  const prevRef = useRef();
  const nextRef = useRef();
  const checkSlidesLength =
    (data?.tipology?.[0] === "HP" && data?.slide?.length > 2) ||
    (data?.tipology?.[0] === "PLP" && data?.slide?.length > 0);

  const onClickCategoryCard = useCallback(
    (i, value) => {
      const index = (i + 1).toString().padStart(2, "0");
      eventInternalLinkClick({
        click_element:
          data?.tipology?.[0] === "HP"
            ? data?.abTestIdentifier
              ? `hp_sbcat_${data.abTestIdentifier}`
              : "hp_sbcat"
            : data?.abTestIdentifier
            ? `plp_sbcat_${data.abTestIdentifier}`
            : "plp_sbcat",
        position: data?.tipology?.[0] === "HP" ? `hp_sbcat_${index}` : `plp_sbcat_${index}`,
        link_text: data?.tipology?.[0] === "HP" ? `hp_sbcat_${value}` : `plp_sbcat_${value}`,
        slug: pagePath,
      });
    },
    [pagePath, data?.abTestIdentifier, data?.tipology]
  );

  return checkSlidesLength ? (
    <>
      {data?.tipology?.[0] === "HP" && data?.anchorDesktop && (
        <span id={slugify(data.anchorDesktop)} className="tabs__scroll-anchor" />
      )}
      <section
        className={classNames(
          `section ${data?.abTestIdentifier}`,
          data?.tipology?.[0] === "PLP" && "section--category-module-plp",
          data?.tipology?.[0] === "HP" && "section--category-module-hp"
        )}
      >
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div
                className={classNames(
                  "category-module",
                  data?.tipology?.[0] === "HP" && "category-module--hp",
                  data?.tipology?.[0] === "PLP" && "category-module--plp",
                  !!data?.activateBackground && "category-module--background-active"
                )}
              >
                <div
                  className={classNames(
                    "category-module__hp-title-wrapper",
                    data?.tipology?.[0] === "PLP" && "category-module__hp-title-wrapper--plp"
                  )}
                >
                  <div className="category-module__hp-title">
                    {data?.tipology?.[0] === "HP" && data?.titleHpVariant
                      ? data.titleHpVariant
                      : ""}
                  </div>
                  <div className="category-module__buttons">
                    <SwiperPrevBtn swiper={swiperData} ref={prevRef} />
                    <SwiperNextBtn swiper={swiperData} ref={nextRef} />
                  </div>
                </div>

                <Swiper
                  centeredSlides={false}
                  onSwiper={(swiper) => setSwiperData(swiper)}
                  scrollbar={{ draggable: false }}
                  slidesPerView={"auto"}
                  spaceBetween={0}
                  navigation={{
                    prevEl: prevRef.current,
                    nextEl: nextRef.current,
                  }}
                  breakpoints={{
                    991: {
                      scrollbar: {
                        draggable: true,
                      },
                      spaceBetween: 0,
                    },
                  }}
                  onInit={(swiper) => {
                    swiper.navigation.prevEl = prevRef.current;
                    swiper.navigation.nextEl = nextRef.current;
                    swiper.navigation.update();
                  }}
                  modules={[Scrollbar, Navigation]}
                >
                  {data?.slide
                    ?.sort((a, b) => {
                      const posA = a?.position?.[0] ? parseInt(a.position?.[0]) : 0;
                      const posB = b?.position?.[0] ? parseInt(b.position?.[0]) : 0;
                      return posA === posB ? 0 : posA < posB ? -1 : 1;
                    })
                    ?.filter((item) => !!item?.image?.node)
                    ?.map((item, i) => (
                      <SwiperSlide className={`${data?.abTestIdentifier}-${i}`} key={i}>
                        <Link
                          to={item?.link}
                          target={item?.target?.[0] || "_self"}
                          className={!item?.link ? "category-module__nolink" : ""}
                          onClick={() => onClickCategoryCard(i, item?.title)}
                        >
                          {data?.tipology?.[0] === "HP" ? (
                            <div className="category-module__card">
                              <GatsbyImage
                                className="category-module__image"
                                image={getImage(item?.image?.node)}
                                alt={item?.image?.value?.description || ""}
                                loading="eager"
                              />
                              {item?.title && (
                                <div className="category-module__title">{item.title}</div>
                              )}
                            </div>
                          ) : (
                            <div className="category-module__card--plp">
                              <GatsbyImage
                                className="category-module__image--plp"
                                image={getImage(item?.image?.node)}
                                alt={item?.image?.value?.description || ""}
                                loading="eager"
                              />
                              {(item?.title || item?.description) && (
                                <div className="category-module__text">
                                  {item?.title && (
                                    <div className="category-module__title--plp text-xs-semibold">
                                      {item.title}
                                    </div>
                                  )}
                                  {item?.description && (
                                    <div
                                      className="category-module__description--plp text-xxs-light"
                                      dangerouslySetInnerHTML={{
                                        __html: item.description,
                                      }}
                                    ></div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </Link>
                      </SwiperSlide>
                    ))}
                </Swiper>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  ) : null;
};

export default CategoryModule;

export const fragment = graphql`
  fragment CategoryModuleFragment on LiferayCategoryModule {
    __typename
    abTestIdentifier
    anchorDesktop
    anchorMobile
    tipology
    activateBackground
    titleHpVariant
    slide {
      title
      description
      image {
        value {
          description
        }
        node {
          publicURL
          gatsbyImageData(layout: FULL_WIDTH, placeholder: NONE, backgroundColor: "#fff")
        }
      }
      link
      position
      target
    }
  }
`;
